import { ENTITY_ITEMS_DROPDOWN_LIMIT } from '@/_legacy/constants';
import { Ref } from 'vue';
import { autocompleteField, checkboxField, dropdownField, inputField } from '@/ui/components/form';
import { Task, useValidation } from '@/ui/concerns';
import { Sensor, validate } from '@/entities/sensor';
import { apiErrorMessage } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { SelectOptions } from '@/ui/types';
import { useDelayRange } from '@/ui/pages/sensor/useDelayRange';
import { chooseUserField } from '@/ui/components/user/ChooseUserField';
import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';

export function useSensorForm(record: Ref<Sensor>, task: Task) {
  const {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  } = useValidation(validate, record, task.state);

  const handleSubmit = () => {
    showValidationErrors.value = true;
    if (hasNoValidationErrors.value) {
      task.run();
    }
  };

  const { getDelayRange } = useDelayRange();

  const formFields = (userOptions: SelectOptions, entityItemOptions: SelectOptions) => [
    autocompleteField({
      name: 'entityItem',
      label: t('common.entity-item'),
      value: record.value.entityItem,
      placeholder: t('sensors.form.enter-entity-item'),
      required: true,
      data: entityItemOptions,
      errors: validationErrors.value,
      disabled: task.state.value.pending,
      limit: ENTITY_ITEMS_DROPDOWN_LIMIT,
    },
    value => {
      record.value.entityItem = value;
    }),

    inputField(
      {
        name: 'title',
        label: t('sensors.title'),
        placeholder: t('sensors.form.enter-title'),
        value: record.value.title,
        required: true,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.title = value;
      },
    ),

    inputField(
      {
        name: 'hardwareId',
        label: t('sensors.hardwareId'),
        placeholder: t('sensors.form.enter-hardwareId'),
        value: record.value.hardwareId,
        required: true,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.hardwareId = value;
      },
    ),

    inputField(
      {
        name: 'temperatureLowerBound',
        label: t('sensors.temperatureLowerBound'),
        placeholder: t('sensors.form.enter-temperatureLowerBound'),
        value: record.value.temperatureLowerBound
          ? record.value.temperatureLowerBound.toString() : '',
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.temperatureLowerBound = parseFloat(value);
      },
    ),

    inputField(
      {
        name: 'temperatureUpperBound',
        label: t('sensors.temperatureUpperBound'),
        placeholder: t('sensors.form.enter-temperatureUpperBound'),
        value: record.value.temperatureUpperBound
          ? record.value.temperatureUpperBound.toString() : '',
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.temperatureUpperBound = parseFloat(value);
      },
    ),

    inputField(
      {
        name: 'humidityLowerBound',
        label: t('sensors.humidityLowerBound'),
        placeholder: t('sensors.form.enter-humidityLowerBound'),
        value: record.value.humidityLowerBound
          ? record.value.humidityLowerBound.toString() : '',
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.humidityLowerBound = parseFloat(value);
      },
    ),

    inputField(
      {
        name: 'humidityUpperBound',
        label: t('sensors.humidityUpperBound'),
        placeholder: t('sensors.form.enter-humidityUpperBound'),
        value: record.value.humidityUpperBound
          ? record.value.humidityUpperBound.toString() : '',
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.humidityUpperBound = parseFloat(value);
      },
    ),

    dropdownField(
      {
        name: 'delayCreatingIssueBy',
        label: t('sensors.delayCreatingIssueBy'),
        value: record.value.delayCreatingIssueBy ?? 0,
        placeholder: t('sensors.form.enter-delayCreatingIssueBy'),
        required: false,
        options: getDelayRange(60),
        errors: validationErrors.value,
        disabled: task.state.value.pending,
        scrollable: true,
        maxHeight: 200,
      },

      value => {
        record.value.delayCreatingIssueBy = value;
      },
    ),

    chooseUserField({
      props: {
        value: record.value.alarmUsers,
        options: userOptions,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
        required: false,
        placeholder: t('sensors.form.enter-users'),
        identifier: CreateUserModelIdentifier.sensorCreator,
      },
      on: {
        input: (value: number[]) => {
          record.value.alarmUsers = value;
        },
      },
    }),

    checkboxField(
      {
        name: 'active',
        label: 'Sensor on/off',
        value: record.value.active,
        required: false,
        disabled: task.state.value.pending,
        errors: validationErrors.value,
      },
      value => {
        record.value.active = value;
      },
    ),

    checkboxField(
      {
        name: 'alarmsActive',
        label: 'Alarms on/off',
        value: record.value.alarmsActive,
        required: false,
        disabled: task.state.value.pending,
        errors: validationErrors.value,
      },
      value => {
        record.value.alarmsActive = value;
      },
    ),

    apiErrorMessage(task.state.value),
  ];

  return {
    formFields,
    handleSubmit,
  };
}
